import React from 'react'
import PropTypes from 'prop-types'
import MuiAutocomplete from '@mui/material/Autocomplete'
import Chip from '@mui/material/Chip'
import { TextField } from '../TextField'
import { tagChipSx } from '../chipStyles'

// Same 40px baseline as Components/ui/Select's own multiple-mode box
// (MULTIPLE_CLOSED_SX) — minHeight instead of a fixed height so the root
// can grow when chips wrap, 4px top/bottom padding tuned against
// tagChipSx's actual rendered Chip height (MUI default "medium", 32px) so
// one row of chips sits flush at 40px, and an error-aware border color so
// no consumer needs to hand-roll this box just to get a red border.
// Deliberately leaves paddingRight untouched: MUI's own outlined-root
// rule reserves extra right padding for the popup-icon adornment via a
// more specific selector (hasPopupIcon chained onto the same root class),
// which a top-level `padding` shorthand here would silently clobber.
const multipleClosedSx = (error) => ({
  '& .MuiOutlinedInput-root': {
    flexWrap: 'wrap',
    boxSizing: 'border-box',
    minHeight: '40px',
    paddingTop: '4px',
    paddingBottom: '4px',
    paddingLeft: '12px',
  },
  '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
    borderColor: error ? 'var(--vo-danger)' : 'var(--vo-line)',
  },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: error ? 'var(--vo-danger)' : 'var(--vo-line)',
  },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: error ? 'var(--vo-danger)' : 'var(--vo-celeste)',
    borderWidth: '1px',
  },
})

// MUI's own default here is reference equality (option === value), which
// silently breaks for any consumer whose value flows through
// react-hook-form's Controller — RHF clones stored field values, so the
// object it hands back is no longer the same reference as the one still
// sitting in `options`, even though it's the same record (GroupsTagInput hit
// this: filterSelectedOptions silently no-opped because two objects with the
// identical _id compared unequal). Reference equality is checked first so
// this is never less correct than MUI's own default; it only adds a
// fallback for the common _id/id-keyed-record shape used throughout this
// codebase. Objects with neither field fall through to false, same as MUI's
// default, so this can't paper over a real mismatch between unrelated
// option shapes. A consumer can still override this via the
// isOptionEqualToValue prop for anything more specific.
const defaultIsOptionEqualToValue = (option, value) => {
  if (option === value) return true
  if (option && value && typeof option === 'object' && typeof value === 'object') {
    if (option._id !== undefined && value._id !== undefined) return option._id === value._id
    if (option.id !== undefined && value.id !== undefined) return option.id === value.id
  }
  return false
}

export const Autocomplete = ({
  options, getOptionLabel, value, onChange, multiple,
  label, placeholder, error, helperText, fullWidth, disabled, loading, name, renderOption,
  filterSelectedOptions, isOptionEqualToValue,
}) => {
  // Single-select only: MUI resyncs its displayed input text from `value`
  // via getOptionLabel whenever `value` changes — including the `null` this
  // component itself produces on keystroke (see onInputChange below). Left
  // uncontrolled, that resync stomps the character the user just typed.
  // isTypingRef + owning `inputValue` ourselves (same mechanism the old
  // DefaultAutocomplete used) tells the resync to back off while the user
  // is actively typing, and re-syncs only for external value changes.
  const [inputValue, setInputValue] = React.useState(
    !multiple && value ? getOptionLabel(value) : ''
  )
  const isTypingRef = React.useRef(false)

  React.useEffect(() => {
    if (multiple) return
    if (!isTypingRef.current) {
      setInputValue(value ? getOptionLabel(value) : '')
    }
  }, [value, multiple, getOptionLabel])

  return (
    <MuiAutocomplete
      options={options}
      getOptionLabel={getOptionLabel}
      value={multiple ? (value || []) : (value ?? null)}
      inputValue={multiple ? undefined : inputValue}
      onChange={(event, newValue) => {
        isTypingRef.current = false
        onChange(newValue)
      }}
      onInputChange={(event, newInputValue, reason) => {
        if (multiple) return
        if (reason === 'input') {
          // Genuine keystroke: clear the selection but keep what was typed.
          isTypingRef.current = true
          setInputValue(newInputValue)
          onChange(null)
        }
        // 'reset' / 'clear': deliberately not handled here. MUI fires
        // 'reset' both for a genuine selection AND as its own side effect
        // of `value` becoming null right after the branch above — using its
        // payload in both cases would re-stomp what the user just typed.
        // The value-driven effect below is the single source of truth for
        // synced display text; the onChange handler flips isTypingRef back
        // to false for real selections so that effect can do its job.
      }}
      multiple={multiple}
      fullWidth={fullWidth}
      disabled={disabled}
      loading={loading}
      sx={multiple ? multipleClosedSx(error) : undefined}
      renderOption={renderOption}
      filterSelectedOptions={filterSelectedOptions}
      isOptionEqualToValue={isOptionEqualToValue}
      renderTags={(tagValue, getTagProps) => tagValue.map((option, index) => (
        <Chip
          key={(option && typeof option === 'object' && (option._id ?? option.id)) ?? getOptionLabel(option)}
          label={getOptionLabel(option)}
          sx={tagChipSx}
          {...getTagProps({ index })}
        />
      ))}
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          // Multiple mode: bypass TextField's own fixed 40px CLOSED_SX on
          // the inner <input> (it fights the flex-wrap chip layout) and let
          // multipleClosedSx above own the 40px baseline on the outer root
          // instead — 'sm' is TextField's existing, already-supported
          // escape hatch for exactly this (see TextField's own multiline ||
          // size === 'sm' skip condition).
          size={multiple ? 'sm' : undefined}
        />
      )}
    />
  )
}

Autocomplete.propTypes = {
  options: PropTypes.array,
  getOptionLabel: PropTypes.func,
  value: PropTypes.any,
  onChange: PropTypes.func,
  multiple: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.node,
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  name: PropTypes.string,
  renderOption: PropTypes.func,
  filterSelectedOptions: PropTypes.bool,
  isOptionEqualToValue: PropTypes.func,
}

Autocomplete.defaultProps = {
  options: [],
  getOptionLabel: (opt) => (opt ? opt.toString() : ''),
  multiple: false,
  error: false,
  filterSelectedOptions: false,
  isOptionEqualToValue: defaultIsOptionEqualToValue,
}
